<template>
  <div id="order-book-table" class="md:tw-flex">
    <div
      class="tw-w-full tw-bg-white tw-border tw-rounded-lg tw-p-4 md:tw-px-10"
    >
      <TableHeader
        :options="options"
        label="options"
        @emitOption="emitOption"
      />
      <div class="tw-w-full tw-overflow-auto">
        <table class="table tw-w-full">
          <thead v-if="tableRows.length !== 0" class="header">
            <tr class="header-row">
              <th
                class="header-data tw-font-normal tw-py-6"
                ref="tableHead"
                :style="gridStyling"
              >
                <div
                  class="tw-justify-self-start"
                  v-for="(header, index) in tableHeaders"
                  :key="'header' + index"
                >
                  {{ header }}
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="table-body">
            <tr class="body-row">
              <td class="body-data tw-h-52 tw-min-h-full" ref="tableRow">
                <div
                  class="data tw-py-6 tw-border-t tw-border-gray-light hover:tw-bg-gray-lightest tw-cursor-pointer"
                  v-for="(details, index) in tableRows"
                  :key="'row' + index"
                  :style="gridStyling"
                  @click="viewOrder(details)"
                >
                  <span></span>
                  <div
                    class="tw-self-center"
                    v-for="(detail, index) in details"
                    :key="'column' + index"
                  >
                    <p
                      v-html="detail"
                      class="tw-text-sm"
                      :class="{
                        [detail.toLowerCase()]:
                          availableStatuses.includes(detail),
                      }"
                    ></p>
                  </div>
                </div>
                <EmptyState v-if="tableRows.length === 0 && !smallLoading">
                  <template v-slot:message>
                    <p class="message tw-text-center tw-text-gray tw-my-3">
                      There has been no orders on your account.
                    </p>
                  </template>
                </EmptyState>
                <SmallLoader v-if="smallLoading && table === 'orderTable'" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-if="this.$route.name !== 'Overview'"
        class="tw-flex tw-justify-end tw-mr-2 tw-mt-16 tw-pb-4"
      >
        <Pagination
          :totalPage="pages"
          :currentPage="page"
          :maxVisible="5"
          @pageChange="gotoPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import TableHeader from "@/components/tables/TableHeader";
import Pagination from "@/components/navigation/Pagination";
import ViewTransactionModal from "@/components/general/MediumModal.vue";
import EmptyState from "@/components/notification/EmptyState.vue";
import { getItem } from "@/utils/storage.js";
import {
  formatISOTime,
  formatISODate,
  formatAmountToDollar,
} from "@/utils/helpers.js";

export default {
  name: "OrderBookTable",

  components: { TableHeader, Pagination, ViewTransactionModal, EmptyState },

  emits: ["emitOption"],

  data() {
    return {
      options: ["Buy Orders", "Sell Orders"],
      currentOption: "",
      availableStatuses: ["Active", "Fulfilled", "Terminated"],
      tableHeaders: [
        "",
        "Amount of token",
        "Price per token",
        "Total",
        "Status",
        "Date & Time",
      ],
      tableRows: [],
      table: "",
    };
  },

  computed: {
    ...mapState({
      smallLoading: (state) => state.smallLoading,
      orderBook: (state) => state.organisationModule.orderBook,
      page: (state) => state.organisationModule.orderPage,
      pages: (state) => state.organisationModule.orderPages,
    }),
    gridStyling() {
      return {
        "grid-template-columns": `${40}px repeat(${5}, ${200}px)`,
      };
    },
  },

  methods: {
    ...mapActions("assetModule", [
      "sellOrderBook",
      "buyOrderBook",
      "getOrderBook",
    ]),

    emitOption(option) {
      this.tableRows = [];
      if (!getItem("org_details").tokens) return;
      const id = getItem("org_details").tokens[0]?._id;
      if (!id) return;
      let type;
      let limit;

      switch (option) {
        case "Buy Orders":
          type = "Buy";
          this.currentOption = "Buy";
          this.table = "orderTable";
          break;
        case "Sell Orders":
          type = "Sell";
          this.currentOption = "Sell";
          this.table = "orderTable";
          break;

        default:
          break;
      }
      // option === "Buy Orders" ? this.buyOrderBook(id) : this.sellOrderBook(id);
      this.$route.name === "Overview" ? (limit = 5) : (limit = 10);
      const payload = { id, page: 1, type, limit: limit };
      this.getOrderBook(payload);
    },

    gotoPage(page) {
      this.tableRows = [];
      this.table = "orderTable";
      const id = getItem("org_details").tokens[0]?._id;
      const payload = {
        id,
        page: page,
        type: this.currentOption,
        limit: 10,
      };
      this.getOrderBook(payload);
    },

    updateTableRow(orders) {
      this.table = "";
      this.tableRows = orders.map((order) => {
        return {
          amount: `<span class="tw-flex"><img
                      src=${order.token.image}
                      class="tw-mr-8 tw-w-8"
                      alt="blue cone"
                      loading="lazy"
                    />${order?.amount.toFixed(2)}</span>`,
          price: formatAmountToDollar(order?.price, 2, "USD"),
          total: formatAmountToDollar(order?.amount * order?.price, 2, "USD"),
          status: order.status,
          date: `${formatISODate(order.createdAt)} ${formatISOTime(
            order.createdAt
          )}`,
        };
      });
    },

    viewOrder() {},
  },

  watch: {
    orderBook(newValue, oldValue) {
      deep: true;
      immediate: true;
      if (newValue !== oldValue) {
        this.updateTableRow(this.orderBook);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#order-book-table {
  @include fadeIn;

  .table {
    .header {
      .header-row {
        .header-data {
          display: grid;
          column-gap: 2rem;
        }
      }
    }
    .table-body {
      .body-row {
        .body-data {
          .data {
            display: grid;
            column-gap: 2rem;
            font-size: 0.7rem;

            // Added with javascript
            .fulfilled,
            .active,
            .terminated {
              text-align: left;
              padding: 0.5rem;
              border-radius: 5px;
            }
            .fulfilled {
              color: $success;
              background-color: $success-light;
              max-width: 4.1rem;
            }
            .active {
              color: $warning;
              background-color: $warning-light;
              max-width: 3.6rem;
            }
            .terminated {
              color: $error;
              background-color: $error-light;
              max-width: 5.4rem;
            }
          }
        }
      }
    }
  }
}
</style>
